import { makeStyles } from '@material-ui/core/styles';

import { DeliveryZone } from '../../types/delivery-zone.type';
import { UpdateDeliveryZoneRequestDTO } from '../../types/dtos/update-delivery-zone.dto';
import { useDeliveryZoneEditForm } from '../../hooks/use-delivery-zone-edit-form.hook';
import { DeliveryZoneDetailsHeader } from './DeliveryZoneDetailsHeader.component';
import { DeliveryZoneInformationsCard } from './DeliveryZoneInformationsCard.component';
import { DeliveryZoneChargesCard } from './DeliveryZoneChargesCard.component';
import { DeliveryZoneCitiesCard } from './DeliveryZoneCitiesCard.component';

type Props = {
  deliveryZone: DeliveryZone;
  isUpdating: boolean;
  updateDeliveryZone: (data: UpdateDeliveryZoneRequestDTO) => Promise<DeliveryZone | null>;
  onArchiveActionClick: () => void;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  body: {
    display: 'flex',
    gap: '2rem',

    '& > *': {
      minWidth: 0,
    },
  },
  formColumn: {
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  citiesColumn: {
    flex: 3,
  },
}));

export function DeliveryZoneInformationsTab({
  deliveryZone,
  isUpdating,
  updateDeliveryZone,
  onArchiveActionClick,
}: Props) {
  const classes = useStyles();
  const {
    updatedDeliveryZone,
    isEditing,
    isNameInvalid,
    areChargesInvalid,
    needsCharges,
    hasInvalidEditedField,
    handleNameChange,
    handleChargeChange,
    handleSave,
    resetForm,
  } = useDeliveryZoneEditForm(deliveryZone, updateDeliveryZone);

  const isArchived = deliveryZone.archivedAt !== null;

  return (
    <section className={classes.root}>
      <DeliveryZoneDetailsHeader
        deliveryZone={deliveryZone}
        isEditing={isEditing}
        isUpdating={isUpdating}
        canSave={isEditing && !isUpdating && !hasInvalidEditedField && !isArchived}
        onCancel={resetForm}
        onSave={handleSave}
        onArchiveActionClick={onArchiveActionClick}
      />
      <div className={classes.body}>
        <div className={classes.formColumn}>
          <DeliveryZoneInformationsCard
            name={updatedDeliveryZone.name}
            isArchived={isArchived}
            isNameInvalid={isNameInvalid}
            onNameChange={handleNameChange}
          />
          <DeliveryZoneChargesCard
            charges={updatedDeliveryZone.charges}
            isArchived={isArchived}
            areChargesInvalid={areChargesInvalid}
            needsCharges={needsCharges}
            onChargeChange={handleChargeChange}
          />
        </div>
        <div className={classes.citiesColumn}>
          <DeliveryZoneCitiesCard zoneId={deliveryZone.id} />
        </div>
      </div>
    </section>
  );
}
